import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack, Tooltip, Touchable, Typography } from '@lyearn/ui';
import { getOptions } from '@/components/Form/components/Frequency/components/InputComponents/Weekdays';
import { DaysOfWeek, daysOfWeekById } from '@/components/Form/components/Frequency/conts';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';
import { CustomFrequencyInput } from '@/types/schema';

export interface Props {
  value: CustomFrequencyInput['daysOfWeek'];
  onChange: (updatedValues: CustomFrequencyInput['daysOfWeek']) => void;
  className?: string;
  error?: React.ReactNode;
  disabled?: boolean;
}

const HabitDaysOfWeek = ({ value, onChange, error, disabled = false }: Props) => {
  const { t } = useTranslation();

  const days = useMemo(
    () => (value ? value.map((dayOfWeek) => daysOfWeekById[dayOfWeek].id) : []),
    [value],
  );

  const onValueChange = useCallback(
    (updatedValue: (typeof DaysOfWeek)[number]['id']) => {
      if (disabled) {
        return;
      }
      if (days.includes(updatedValue)) {
        return onChange([...days.filter((day) => day !== updatedValue)]);
      }
      onChange([...days, updatedValue]);
    },
    [days, disabled, onChange],
  );

  const options = useMemo(() => getOptions(DaysOfWeek, t), [t]);

  return (
    <Stack direction="col" gapY={8}>
      <Stack className="px-4" justifyContent="between">
        {options.map((option) => {
          const isSelected = days.includes(option.value);
          return (
            <Tooltip key={option.value} title={getSelectOptionLabel(option)}>
              <Touchable
                className={cn(
                  'pointer flex h-48 w-48 items-center justify-center  rounded-full border border-border',
                  isSelected ? 'bg-focus-primary' : 'bg-bg-primary',
                  { 'hover:border-border-medium hover:bg-bg-secondary': !isSelected },
                )}
                onClick={() => onValueChange(option.value)}>
                <Typography
                  color={isSelected ? 'text-text-white' : 'text-text-secondary'}
                  variant="label-01">
                  {getSelectOptionLabel(option).slice(0, 1)}
                </Typography>
              </Touchable>
            </Tooltip>
          );
        })}
      </Stack>
      {error ? (
        <Typography className="ms-8" color="text-text-error" variant="body-short-03">
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
};

export default HabitDaysOfWeek;
